import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import { Toaster as Sonner } from "./components/ui/Sonner.jsx";
import { Toaster } from "./components/ui/Toaster.jsx";
import { TooltipProvider } from "./components/ui/ToolTip.jsx";

import LayoutProvider from "./components/layout/LayoutProvider.jsx";
import OutletProvider from "./components/layout/OutletProvider.jsx";
import {
  default as AddCompany,
  default as CreateCompany,
} from "./pages/AddCompany.jsx";
import AddLead from "./pages/AddLead.jsx";
import AddTeamMember from "./pages/AddTeamMember.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import ForgotPassword from "./pages/ForgotPassword.jsx";
import LeadDetails from "./pages/LeadDetails.jsx";
import Leads from "./pages/Leads.jsx";
import Login from "./pages/Login.jsx";
import NotFound from "./pages/NotFound";
import Profile from "./pages/Profile.jsx";
import SendOTP from "./pages/SendOTP.jsx";
import SetPassword from "./pages/SetPassword.jsx";
import Team from "./pages/Team.jsx";
import TeamMemberProfile from "./pages/TeamMemberProfile.jsx";
import ValidateOTP from "./pages/ValidateOTP.jsx";
import VerifyOTP from "./pages/VerifyOTP.jsx";
import { getAuthToken, getCurrentUser, getSuperAdminToken } from "./services/authService.js";
import TeamDetails from "./pages/TeamDetails.jsx";
import Settings from "./pages/Settings.jsx";
import Account from "./pages/Account.jsx";
import AuditLogs from "./pages/AuditLogs.jsx";
import SuperAdminLogin from "./pages/super-admin/SuperAdminLogin.jsx";
import SuperAdminLayout from "./pages/super-admin/SuperAdminLayout.jsx";
import SuperAdminDashboard from "./pages/super-admin/SuperAdminDashboard.jsx";
import UserManagement from "./pages/super-admin/UserManagement.jsx";
import CompanyManagement from "./pages/super-admin/CompanyManagement.jsx";
import ActivityLogs from "./pages/super-admin/ActivityLogs.jsx";
import IndustryTemplates from "./pages/super-admin/IndustryTemplates.jsx";
import Trash from "./pages/super-admin/Trash.jsx";
import { Loader, Loader2 } from "lucide-react";

const queryClient = new QueryClient();

const ProtectedRoute = ({ children, allowedRoles }) => {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    const token = getAuthToken();
    // If token doesn't exist, redirect to login
    if (!token) {
      navigate("/", { replace: true });
      return;
    }
    getCurrentUser()
      .then((data) => {
        if (data.success && data.user && data.user.user_role) {
          setUserRole(data.user.user_role);
        } else {
          setUserRole(null);
        }
        setLoading(false);
      })
      .catch(() => {
        setUserRole(null);
        setLoading(false);
      });
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center w-screen h-[100dvh]">
        {/* <Loader2 /> */}
      </div>
    );

  if (!userRole) {
    return <>{children}</>;
  }

  if (Array.isArray(allowedRoles) && !allowedRoles.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const PublicRoute = ({ children }) => {
  const token = getAuthToken();

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const SuperAdminProtectedRoute = ({ children }) => {
  const token = getSuperAdminToken();

  if (!token) {
    return <Navigate to="/super-admin/login" replace />;
  }

  // Decode and verify role from token
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.user_role !== "super_admin") {
      return <Navigate to="/super-admin/login" replace />;
    }
  } catch {
    return <Navigate to="/super-admin/login" replace />;
  }

  return children;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* <Route path="/" element={<Login />} /> */}
            <Route
              path="/"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute
                  allowedRoles={["business_admin", "sales_person"]}
                >
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <Leads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lead/:leadId"
              element={
                <ProtectedRoute>
                  <LeadDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            {/* <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            /> */}
            <Route
              path="/team"
              element={
                <ProtectedRoute allowedRoles={["business_admin"]}>
                  <Team />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/:memberId"
              element={
                <ProtectedRoute>
                  {/* <LeadDetails /> kajdhfhsfh */}
                  <TeamDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={["business_admin"]}>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute allowedRoles={["business_admin"]}>
                  <AuditLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create-company"
              element={
                <ProtectedRoute>
                  <CreateCompany />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-lead"
              element={
                <ProtectedRoute
                  allowedRoles={["business_admin", "sales_person"]}
                >
                  <AddLead />
                </ProtectedRoute>
              }
            />
            <Route element={<LayoutProvider />}>
              <Route path="/send-otp" element={<SendOTP />} />
              <Route path="/validate-otp" element={<ValidateOTP />} />
              <Route path="/add-team-member" element={<AddTeamMember />} />
              <Route path="/add-company" element={<AddCompany />} />
            </Route>

            <Route element={<OutletProvider />}>
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-otp" element={<VerifyOTP />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/add-team-member" element={<AddTeamMember />} />
            </Route>
            <Route
              path="/team-member/:id"
              element={
                <ProtectedRoute allowedRoles={["business_admin"]}>
                  <TeamMemberProfile />
                </ProtectedRoute>
              }
            />
            {/* Super Admin Routes */}
            <Route path="/super-admin/login" element={<SuperAdminLogin />} />
            <Route
              path="/super-admin"
              element={
                <SuperAdminProtectedRoute>
                  <SuperAdminLayout />
                </SuperAdminProtectedRoute>
              }
            >
              <Route path="dashboard" element={<SuperAdminDashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="companies" element={<CompanyManagement />} />
              <Route path="activity-logs" element={<ActivityLogs />} />
              <Route path="industry-templates" element={<IndustryTemplates />} />
              <Route path="trash" element={<Trash />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
